import { Tracking } from '@/shared/services/tracking';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse, get } from '@truefit/http-utils';
import { AxiosError } from 'axios';


interface DownloadParams {
  clipId: string;
  video: boolean;
  /**
   * Which UI surface started the download. The same event fires from four
   * entry points, which are otherwise indistinguishable in PostHog. Required
   * so a new call site cannot silently emit an unattributed `Download Clip`.
   */
  source: ClipDownloadSource;
}

export type ClipDownloadSource =
  | 'clip_downloads_panel'
  | 'clip_library'
  | 'clips_table'
  | 'download_clip_dialog'
  | 'time_machine';

interface DownloadResponse {
  downloadUrl: string;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const fetchDownloadKey = async ({ clipId, video, source }: DownloadParams): Promise<string> => {
  const endpoint = `/v1.0/clips/${clipId}/download?${video ? 'video=true' : 'metadata=true'}`;

  let attempt = 0;
  while (attempt < MAX_RETRIES) {
    try {
      const { data }: AxiosResponse<DownloadResponse> = await get(endpoint, () => ({
        maxRedirects: 0,
      }));

      if (!data.downloadUrl) {
        throw new Error('Download key not found in response');
      }

      Tracking.getInstance().track('Download Clip', {
        category: 'Clips',
        source,
        video,
      });
      return data.downloadUrl;
    } catch (error) {
      attempt++;

      // If it's the last attempt, throw the error
      if (attempt === MAX_RETRIES) {
        throw error;
      }

      // Only retry on specific errors
      const axiosError = error as AxiosError;
      if (
        axiosError.response?.status &&
        ![408, 429, 500, 502, 503, 504].includes(axiosError.response.status)
      ) {
        throw error;
      }

      // Exponential backoff
      await sleep(RETRY_DELAY * Math.pow(2, attempt - 1));
    }
  }

  throw new Error('Maximum retry attempts reached');
};

const useClipDownloadMutation = () => {
  return useMutation({
    mutationFn: async ({ clipId, video, source }: DownloadParams) => {
      try {
        const downloadKey = await fetchDownloadKey({ clipId, video, source });

        // Trigger download using programmatic anchor click
        // IMPORTANT: The 'download' attribute tells the browser to download rather than navigate
        // Without it, the browser treats this as navigation which can kill WebRTC connections
        const downloadUrl = downloadKey.replace('/business-api/v1.0/', '/unrestricted/v1.0/');
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = ''; // Empty string = use server-provided filename, but signals "download intent"
        anchor.target = '_blank'; // Additional safeguard: open in new context if download fails
        anchor.rel = 'noopener noreferrer'; // Security best practice for target="_blank"
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        return downloadKey;
      } catch (error) {
        // Transform error messages for better UX
        if (error instanceof AxiosError) {
          switch (error.response?.status) {
            case 404:
              throw new Error('Clip not found');
            case 403:
              throw new Error('You do not have permission to download this clip');
            case 429:
              throw new Error('Too many requests. Please try again later');
            default:
              throw new Error('Failed to download clip. Please try again');
          }
        }
        throw error;
      }
    },
    gcTime: 0,
    retry: false, // We handle retries manually for more control
  });
};

export default useClipDownloadMutation;